import { useEffect, useRef, useState, type ReactNode } from 'react'
import { RowMenu } from '@/components/RowMenu'
import { useMinWidth } from '@/lib/useMediaQuery'

/**
 * The repeatable rows of a consultation — the prescription and the test
 * results — in the shape the screen can take.
 *
 * From `md` up it is a grid, which is what a doctor comparing four medicines
 * wants. Below `md` each row becomes a labelled block of fields. Five editable
 * columns inside a 390px card did not merely look tight: the uppercase headers
 * overlapped into "FREQUENCYDURATIONREMARK" and every value was cut to four
 * characters, so the row could be typed into but not read back.
 *
 * One field list drives both, so a column cannot exist on a desktop and be
 * missing from the phone — and the accessible name of a cell is the same in
 * either posture, which is what the row's Enter-to-next-field behaviour and
 * every test address it by.
 */

export interface RowField<Row> {
  key: string
  /** The column heading, and the visible label above the field on a phone. */
  header: string
  /** Width in the desktop grid, as a `<col>` class. */
  width: string
  /** Short values sit two across on a phone; a drug name takes the full width. */
  half?: boolean
  /** The accessible name for this field in row `index` (0-based). */
  label: (index: number) => string
  render: (args: {
    row: Row
    index: number
    /** Borderless in the grid, bordered on a phone where there is no grid to
     *  imply the field's edges. */
    className: string
    label: string
    onKeyDown: (event: React.KeyboardEvent) => void
    set: (changes: Partial<Row>) => void
  }) => ReactNode
}

/** Borderless until focused: the grid reads as a table, not as a wall of
 *  boxes, and the cell still shows exactly where the caret is. */
const GRID_CELL =
  'min-h-touch w-full min-w-0 border-0 bg-transparent px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:rounded-sm focus:bg-surface focus:outline-2 focus:-outline-offset-2 focus:outline-focus'

/** On a phone there is no grid to draw the field's edges, so the field draws
 *  its own. 16px, not 15: below that iOS Safari zooms the page on focus. */
const STACKED_CELL =
  'min-h-touch w-full min-w-0 rounded-md border border-control bg-surface px-3 py-2 text-base text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-2 focus:-outline-offset-2 focus:outline-focus'

interface Props<Row> {
  /** Names the region, and each row for its own menu — "Medicine", "Test". */
  noun: string
  /** The region's accessible name — "Prescription", "Test results". */
  regionLabel: string
  fields: RowField<Row>[]
  items: Row[]
  onChange: (items: Row[]) => void
  blank: () => Row
  empty: string
  /** The field a new row lands in, by key. */
  focusField: string
}

export function RowEditor<Row>({
  noun,
  regionLabel,
  fields,
  items,
  onChange,
  blank,
  empty,
  focusField,
}: Props<Row>) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [focusRow, setFocusRow] = useState<number | null>(null)
  const wide = useMinWidth('md')
  const first = fields.find((field) => field.key === focusField) ?? fields[0]!

  // Focus lands on the new row only after it has rendered, so the row is looked
  // up by the same accessible name the rest of the app addresses it by.
  useEffect(() => {
    if (focusRow === null) return
    wrapRef.current
      ?.querySelector<HTMLInputElement>(`[aria-label="${first.label(focusRow)}"]`)
      ?.focus()
    setFocusRow(null)
  }, [focusRow, first])

  function set(index: number, changes: Partial<Row>) {
    onChange(items.map((item, i) => (i === index ? { ...item, ...changes } : item)))
  }

  function remove(index: number) {
    onChange(items.filter((_, i) => i !== index))
  }

  /** Enter adds a row; it never saves. These sit inside the consultation
   *  <form>, so an unhandled Enter would submit it mid-prescription.
   *  Ctrl/Cmd+Enter is the deliberate save and is left alone. */
  function onCellKeyDown(event: React.KeyboardEvent, index: number) {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return
    event.preventDefault()
    if (index === items.length - 1) {
      onChange([...items, blank()])
      setFocusRow(items.length)
    } else {
      setFocusRow(index + 1)
    }
  }

  const menu = (index: number) => (
    <RowMenu
      label={`Actions for ${noun.toLowerCase()} ${index + 1}`}
      items={[{ label: 'Remove', danger: true, onSelect: () => remove(index) }]}
    />
  )

  if (!wide) {
    return (
      <div ref={wrapRef} role="group" aria-label={regionLabel}>
        {items.length === 0 ? (
          <p className="py-5 text-center text-sm text-fg-subtle">{empty}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-4 p-0">
            {items.map((row, index) => (
              <li
                key={index}
                className="grid grid-cols-2 gap-3 not-first:border-t not-first:border-line-soft not-first:pt-4"
              >
                <div className="col-span-2 flex min-h-9 items-center justify-between gap-2">
                  <span className="text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
                    {noun} {index + 1}
                  </span>
                  {menu(index)}
                </div>
                {fields.map((field) => (
                  <label
                    key={field.key}
                    className={field.half ? 'flex flex-col gap-1' : 'col-span-2 flex flex-col gap-1'}
                  >
                    {/* A real visible label, not a column header three rows
                        above: on a phone the header is off the screen by the
                        time the field is in the thumb's reach. */}
                    <span className="text-xs font-medium text-fg-muted">{field.header}</span>
                    {field.render({
                      row,
                      index,
                      className: STACKED_CELL,
                      label: field.label(index),
                      onKeyDown: (event) => onCellKeyDown(event, index),
                      set: (changes) => set(index, changes),
                    })}
                  </label>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    // tabIndex: the grid can still scroll sideways on a small laptop, and a
    // scrollable region that cannot be reached by keyboard is unreachable
    // content. `relative` keeps an `sr-only` label inside the scroller rather
    // than letting it stretch the document — see `DataTable`.
    <div
      className="relative overflow-x-auto overscroll-x-contain"
      ref={wrapRef}
      tabIndex={0}
      role="region"
      aria-label={regionLabel}
    >
      <table className="w-full table-fixed border-collapse text-sm [&_thead_th]:bg-subtle [&_thead_th]:px-2 [&_thead_th]:py-2 [&_thead_th]:text-left [&_thead_th]:text-2xs [&_thead_th]:leading-tight [&_thead_th]:font-semibold [&_thead_th]:tracking-wide [&_thead_th]:text-fg-muted [&_thead_th]:uppercase [&_thead_th:first-child]:rounded-l-sm [&_thead_th:first-child]:pl-3 [&_thead_th:last-child]:rounded-r-sm [&_tbody_td]:border-t [&_tbody_td]:border-line-soft [&_tbody_td]:p-0 [&_th:last-child]:sticky [&_th:last-child]:right-0 [&_th:last-child]:w-touch [&_th:last-child]:pr-0 [&_th:last-child]:text-right [&_td:last-child]:sticky [&_td:last-child]:right-0 [&_td:last-child]:w-touch [&_td:last-child]:bg-surface [&_td:last-child]:pr-0 [&_td:last-child]:text-right">
        <colgroup>
          {fields.map((field) => (
            <col key={field.key} className={field.width} />
          ))}
          <col className="w-touch" />
        </colgroup>
        <thead>
          <tr>
            {fields.map((field) => (
              <th key={field.key} scope="col">
                {field.header}
              </th>
            ))}
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={fields.length + 1} className="p-5 text-center text-sm text-fg-subtle">
                {empty}
              </td>
            </tr>
          ) : (
            items.map((row, index) => (
              // Index as key: rows are ordered and a drug name is empty until
              // typed, so there is no stable id before the visit is saved.
              <tr key={index}>
                {fields.map((field) => (
                  <td key={field.key}>
                    {field.render({
                      row,
                      index,
                      className: GRID_CELL,
                      label: field.label(index),
                      onKeyDown: (event) => onCellKeyDown(event, index),
                      set: (changes) => set(index, changes),
                    })}
                  </td>
                ))}
                <td>{menu(index)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
